import React, { useEffect, useRef, useState } from "react";
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { MapPin, Globe, Phone, Mail, Instagram, Facebook, CheckCircle2, Calendar, Sparkles } from "lucide-react";
import { api, resolveImageUrl } from "@/lib/api";
import { useT } from "@/context/LangContext";
import RedeemDialog from "@/components/RedeemDialog";

export default function PartnerDetailDialog({ partner, open, onOpenChange }) {
  const { t } = useT();
  const [redeemOpen, setRedeemOpen] = useState(false);
  const trackedViewRef = useRef(null);

  // Track unique view per dialog open
  useEffect(() => {
    if (!open || !partner?.id) return;
    if (trackedViewRef.current === partner.id) return;
    trackedViewRef.current = partner.id;
    api.post(`/partners/${partner.id}/track`, { event: "view" }).catch(() => {});
  }, [open, partner?.id]);

  if (!partner) return null;

  const handleClaim = () => {
    api.post(`/partners/${partner.id}/track`, { event: "redeem" }).catch(() => {});
    setRedeemOpen(true);
  };

  return (
    <>
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl p-0 overflow-hidden rounded-2xl" data-testid={`partner-dialog-${partner.id}`}>
        <DialogTitle className="sr-only">{partner.name}</DialogTitle>
        <DialogDescription className="sr-only">{partner.offer}</DialogDescription>
        <div className="relative aspect-[21/9] bg-muted">
          <img src={resolveImageUrl(partner.image_url)} alt={partner.name} className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-t from-navy-900 via-navy-900/40 to-transparent" />
          <div className="absolute bottom-0 inset-x-0 p-6 text-white">
            <div className="flex flex-wrap gap-1.5 mb-2">
              {(partner.badges || []).map((b) => (
                <span key={b} className="chip bg-lime-500 text-navy-900">{b}</span>
              ))}
            </div>
            <h2 className="font-heading font-extrabold text-3xl md:text-4xl tracking-tight">{partner.name}</h2>
          </div>
        </div>
        <div className="p-6 md:p-8 max-h-[60vh] overflow-y-auto">
          <div className="rounded-2xl bg-lime-500/10 border border-lime-500/30 p-5 mb-6">
            <div className="flex items-start gap-3">
              <Sparkles className="w-5 h-5 text-lime-600 mt-0.5" />
              <div>
                <div className="text-xs uppercase tracking-widest text-navy-900/70 font-semibold">{t("dialog.offer_label")}</div>
                <div className="font-heading font-bold text-xl text-navy-900 mt-1">{partner.offer}</div>
                {partner.offer_value > 0 && (
                  <div className="text-xs text-muted-foreground mt-2">{t("dialog.estimated_value", { amount: partner.offer_value })}</div>
                )}
              </div>
            </div>
          </div>

          <div className="grid md:grid-cols-2 gap-6">
            <div>
              <h3 className="font-heading font-bold text-navy-900 mb-2">{t("dialog.about")}</h3>
              <p className="text-sm text-muted-foreground leading-relaxed">{partner.long_description || partner.description}</p>

              {partner.conditions && (
                <>
                  <h3 className="font-heading font-bold text-navy-900 mt-5 mb-2">{t("dialog.conditions")}</h3>
                  <p className="text-sm text-muted-foreground leading-relaxed">{partner.conditions}</p>
                </>
              )}
            </div>
            <div className="space-y-3 text-sm">
              <div className="flex gap-2 text-navy-900"><MapPin className="w-4 h-4 mt-0.5 text-lime-600" /><span>{partner.address}<br/>{partner.city}</span></div>
              {partner.phone && <div className="flex gap-2 text-navy-900"><Phone className="w-4 h-4 mt-0.5 text-lime-600" /><a href={`tel:${partner.phone}`} className="hover:underline">{partner.phone}</a></div>}
              {partner.email && <div className="flex gap-2 text-navy-900"><Mail className="w-4 h-4 mt-0.5 text-lime-600" /><a href={`mailto:${partner.email}`} className="hover:underline">{partner.email}</a></div>}
              {partner.website && <div className="flex gap-2 text-navy-900"><Globe className="w-4 h-4 mt-0.5 text-lime-600" /><a href={partner.website} target="_blank" rel="noreferrer" className="hover:underline break-all">{partner.website}</a></div>}
              {partner.instagram && <div className="flex gap-2 text-navy-900"><Instagram className="w-4 h-4 mt-0.5 text-lime-600" /><a href={partner.instagram} target="_blank" rel="noreferrer" className="hover:underline break-all">{partner.instagram}</a></div>}
              {partner.facebook && <div className="flex gap-2 text-navy-900"><Facebook className="w-4 h-4 mt-0.5 text-lime-600" /><a href={partner.facebook} target="_blank" rel="noreferrer" className="hover:underline break-all">{partner.facebook}</a></div>}
              {partner.end_date && <div className="flex gap-2 text-navy-900"><Calendar className="w-4 h-4 mt-0.5 text-lime-600" /><span>{t("dialog.offer_valid_until", { date: partner.end_date })}</span></div>}
              {partner.verified && <div className="flex gap-2 text-emerald-700"><CheckCircle2 className="w-4 h-4 mt-0.5" /><span className="font-semibold">{t("dialog.verified")}</span></div>}
            </div>
          </div>

          <div className="mt-7 flex flex-col sm:flex-row gap-3">
            <button
              className="btn-lime flex-1"
              data-testid={`partner-claim-${partner.id}`}
              onClick={handleClaim}
            >
              {t("dialog.claim")}
            </button>
            <a
              href={`https://www.google.com/maps/search/${encodeURIComponent(partner.address + " " + partner.city)}`}
              target="_blank"
              rel="noreferrer"
              className="btn-ghost"
              data-testid={`partner-directions-${partner.id}`}
            >
              {t("dialog.directions")}
            </a>
          </div>
        </div>
      </DialogContent>
    </Dialog>
    <RedeemDialog partner={partner} open={redeemOpen} onOpenChange={setRedeemOpen} />
    </>
  );
}
